/**
 * EA bundle discount — cart helpers for Horizon / theme assets.
 *
 * Adds cart lines tagged with `_item_type: "bundle_line_item"` so the discount
 * function can qualify them (still requires eligible products + >= 2 lines).
 *
 * Load in theme (e.g. theme.liquid before </body>):
 *   {{ 'bundle-discount-cart.js' | asset_url | script_tag }}
 *
 * Every bundle line MUST carry the variant's EA marketplace id. The cart drawer's
 * pre-checkout gate (`#buildAvailabilityProductIds` in assets/ea-cart-drawer.js) reads a
 * `bundle_line_item` with no `_external_variant_id` as a STALE line and CLEARS THE WHOLE
 * CART — so such a line is not a degraded line, it is a cart wipe waiting for the next
 * drawer open. These helpers therefore take `{ id, externalVariantId }` entries and throw
 * rather than post an unidentified line, matching the refusals in assets/cart-add-more.js
 * and the cart drawer's own `#addBundleVariant`.
 *
 * Browser console:
 *   await EABundleDiscount.addBundleToCart([
 *     { id: 401234567890, externalVariantId: 'US-18517' },
 *     { id: 401234567891, externalVariantId: 'US-18518' },
 *   ])
 *   await EABundleDiscount.addBundleLineItem({ id: 401234567890, externalVariantId: 'US-18517' }, 2)
 */
(function () {
  const PROPERTY_KEY = '_item_type';
  const BUNDLE_ITEM_TYPE = 'bundle_line_item';
  const EXTERNAL_VARIANT_ID_KEY = '_external_variant_id';

  /**
   * @param {string} externalVariantId country-prefixed marketplace id, e.g. "US-18517"
   * @returns {Record<string, string>}
   */
  function bundleLineProperties(externalVariantId) {
    return {
      [PROPERTY_KEY]: BUNDLE_ITEM_TYPE,
      [EXTERNAL_VARIANT_ID_KEY]: externalVariantId,
    };
  }

  /**
   * @typedef {{ id: number|string, externalVariantId: string }} BundleEntry
   */

  /**
   * @param {BundleEntry|Array<BundleEntry>} entries
   * @param {number} [quantity=1] per variant
   * @returns {Array<{ id: number, quantity: number, properties: Record<string, string> }>}
   * @throws {Error} when any entry carries no usable `externalVariantId` — fail closed,
   *   so a caller that omits one gets a loud error instead of a cart that empties itself.
   */
  function toBundleCartItems(entries, quantity = 1) {
    const list = Array.isArray(entries) ? entries : [entries];
    const qty = Math.max(1, Number(quantity) || 1);

    return list.map((entry) => {
      const isEntry = entry !== null && typeof entry === 'object';
      const id = isEntry ? entry.id : entry;
      // Blank is absent: stamping "" would satisfy the letter of the contract while still
      // failing the availability lookup, and still tripping the stale-bundle wipe.
      const externalVariantId = isEntry ? String(entry.externalVariantId || '').trim() : '';

      if (!externalVariantId) {
        throw new Error(
          `EABundleDiscount: variant ${id} has no externalVariantId. A bundle line ` +
            `without ${EXTERNAL_VARIANT_ID_KEY} is read as stale by the cart drawer and ` +
            `clears the cart. Pass { id, externalVariantId: 'US-…' }.`,
        );
      }

      return {
        id: Number(id),
        quantity: qty,
        properties: bundleLineProperties(externalVariantId),
      };
    });
  }

  /**
   * POST JSON to Cart AJAX API.
   * @param {string} path e.g. "cart/add.js"
   * @param {object} body
   */
  async function cartFetch(path, body) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const text = await response.text();
    let data;

    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text;
    }

    if (!response.ok) {
      const message = (data && typeof data === 'object' && data.description) || (typeof data === 'string' ? data : response.statusText);
      throw new Error(message || 'Cart request failed');
    }

    return data;
  }

  /**
   * Add one bundle line item.
   * @param {BundleEntry} entry `{ id, externalVariantId }`
   * @param {number} [quantity=1]
   * @returns {Promise<object>}
   * @throws {Error} when the entry carries no `externalVariantId`
   */
  async function addBundleLineItem(entry, quantity = 1) {
    return cartFetch('cart/add.js', {
      items: toBundleCartItems(entry, quantity),
    });
  }

  /**
   * Add multiple bundle line items in one request (typical bundle builder).
   * @param {Array<BundleEntry>} entries `{ id, externalVariantId }` per variant
   * @param {number} [quantity=1] quantity applied to each variant
   * @returns {Promise<object>}
   * @throws {Error} when any entry carries no `externalVariantId`
   *
   * @example
   * await EABundleDiscount.addBundleToCart([
   *   { id: 401234567890, externalVariantId: 'US-18517' },
   *   { id: 401234567891, externalVariantId: 'US-18518' },
   * ])
   */
  async function addBundleToCart(entries, quantity = 1) {
    if (!Array.isArray(entries) || entries.length === 0) {
      throw new Error('addBundleToCart expects a non-empty array of { id, externalVariantId } entries');
    }

    return cartFetch('cart/add.js', {
      items: toBundleCartItems(entries, quantity),
    });
  }

  window.EABundleDiscount = {
    PROPERTY_KEY,
    BUNDLE_ITEM_TYPE,
    addBundleLineItem,
    addBundleToCart,
    toBundleCartItems,
  };
})();
